import axios from "axios";
import { useState, useCallback } from "react";
import 'animate.css';
import { useAuth } from '../guardarToken/authContext';
import Spinner from '../spinner/Spinner';
import { Button } from '../UI/button';
import { Input } from '../UI/input';
import { Label } from '../UI/label';
import {
  Lock,
  User,
  ChevronRight,
  ShieldCheck,
  Building2,
  AlertCircle,
  Eye,
  EyeOff
} from 'lucide-react';
import logoCommenda from '../barradeNavegacion/img/commanda-app.png';

const apiUrlLogin = process.env.REACT_APP_API_LOGIN;

function Login() {
  const [usuario, setUsuario] = useState('');
  const [pass, setPass] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { setTipoUsuario, handleAuthenticationSuccess } = useAuth();
  const [error, setError] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  const handleLogin = useCallback(async () => {
    setError(false);
    if (!usuario || !pass) return;

    setShowSpinner(true);
    // Simular un poco de delay para el efecto dramático de carga premium
    setTimeout(async () => {
      try {
        const response = await axios.post(apiUrlLogin, { usuario, pass });
        const { token, tipoUsuario, nombreUsuario, nombreuser, apellido, sector, id } = response.data;

        console.log('Respuesta del login:', response.data);

        localStorage.setItem('nombreUsuario', nombreUsuario);
        localStorage.setItem('tipoUsuario', tipoUsuario);
        localStorage.setItem('nombre', nombreuser || '');
        localStorage.setItem('apellido', apellido || '');
        localStorage.setItem('sector', sector || '');
        localStorage.setItem('id', id);

        handleAuthenticationSuccess({ token, tipoUsuario, nombreUsuario, nombreuser, apellido, sector, id });
        setTipoUsuario(tipoUsuario);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (err) {
        setError(true);
        console.error('Login error:', err);
      } finally {
        setShowSpinner(false);
      }
    }, 1500);
  }, [usuario, pass, setTipoUsuario, handleAuthenticationSuccess]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (showSpinner) return;
    handleLogin();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 relative overflow-hidden font-sans">
      {/* BACKGROUND EFFECTS */}
      <div className="absolute top-0 -left-4 w-72 h-72 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob" />
      <div className="absolute top-0 -right-4 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000" />
      <div className="absolute -bottom-8 left-20 w-72 h-72 bg-fuchsia-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000" />

      {/* VIDEO BACKGROUND FRAMED */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none p-4 md:p-8">
        <div className="relative w-full h-full max-w-7xl max-h-[90vh] overflow-hidden rounded-[3rem] shadow-[0_0_50px_rgba(0,0,0,0.5)]">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-60"
          >
            <source src="/login_video.mp4" type="video/mp4" />
          </video>
          {/* Sutil gradiente para integrar con el fondo oscuro */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/20 via-transparent to-slate-950/40" />
        </div>
      </div>

      <div className="w-full max-w-md px-6 z-10">
        {/* LOGO AREA */}
        <div className="flex flex-col items-center mb-8 animate__animated animate__fadeInDown">
          <div className="bg-white p-4 rounded-3xl shadow-2xl shadow-emerald-500/20 ring-1 ring-black/5 mb-4 transform hover:rotate-3 transition-transform">
            <img
              src={logoCommenda}
              alt="Logo"
              className="h-16 w-16 object-contain"
              draggable={false}
            />
          </div>
          <h1 className="text-2xl font-black tracking-tight text-white mb-1">Commenda App</h1>
          <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
            <ShieldCheck className="w-3 h-3 text-emerald-500" />
            <span className="text-[9px] font-black uppercase tracking-widest text-emerald-500">Acceso Seguro</span>
          </div>
        </div>

        {/* LOGIN CARD */}
        <div className="relative animate__animated animate__fadeInUp">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-500 to-indigo-500 rounded-3xl blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>

          <div className={`relative bg-slate-900/80 backdrop-blur-xl border border-white/10 p-8 rounded-3xl shadow-2xl overflow-hidden ${showSpinner ? "min-h-[350px] flex items-center justify-center" : ""}`}>

            {showSpinner ? (
              <div className="flex flex-col items-center text-center space-y-4 animate__animated animate__zoomIn">
                <Spinner size={40} className="text-emerald-500" />
                <div className="space-y-1">
                  <p className="text-sm font-black uppercase tracking-[0.2em] text-white">Verificando</p>
                  <p className="text-[10px] font-bold text-slate-500">Estableciendo conexión segura...</p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="space-y-2">
                  <h2 className="text-xl font-bold text-white">Bienvenido</h2>
                  <p className="text-xs font-medium text-slate-400">Ingresa tus credenciales para continuar</p>
                </div>

                <form className="space-y-6" onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="usuario" className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Usuario</Label>
                      <div className="relative group">
                        <User className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 transition-colors group-focus-within:text-emerald-500 z-20" />
                        <Input
                          id="usuario"
                          type="text"
                          placeholder="ej. pgarcia"
                          value={usuario}
                          onChange={e => setUsuario(e.target.value)}
                          autoComplete="username"
                          className="bg-slate-950/50 border-white/5 focus:border-emerald-500/50 text-white !pl-16 h-12 rounded-xl transition-all"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="pass" className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Contraseña</Label>
                      <div className="relative group">
                        <Lock className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 transition-colors group-focus-within:text-emerald-500 z-20" />
                        <Input
                          id="pass"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={pass}
                          onChange={e => setPass(e.target.value)}
                          autoComplete="current-password"
                          className="bg-slate-950/50 border-white/5 focus:border-emerald-500/50 text-white !pl-16 !pr-14 h-12 rounded-xl transition-all"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-400 transition-colors z-20"
                          aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-center gap-3 animate__animated animate__shakeX">
                      <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
                      <p className="text-[10px] font-black uppercase tracking-wider text-red-500">Credenciales incorrectas</p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full h-12 bg-gradient-to-r from-emerald-600 to-indigo-600 hover:from-emerald-50 to-indigo-700 text-white font-black text-[11px] uppercase tracking-[0.2em] rounded-xl shadow-xl shadow-emerald-950/20 transform hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                    disabled={showSpinner}
                  >
                    Continuar <ChevronRight className="w-4 h-4" />
                  </Button>
                </form>

                <div className="pt-4 border-t border-white/5 flex items-center justify-between text-[9px] font-black uppercase tracking-widest text-slate-600">
                  <div className="flex items-center gap-1.5">
                    <Building2 className="w-3 h-3" />
                    <span>Commanda</span>
                  </div>
                  <span>v1.0.0 Prod</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <p className="mt-8 text-center text-[10px] font-black uppercase tracking-widest text-slate-700">
          Def-Software © 2025 • Todos los derechos reservados
        </p>
      </div>
    </div>
  );
}

export default Login;
